import { Link } from "react-router-dom";
import VaultLogo from "@/components/VaultLogo";

/**
 * Terms of Service. Plain-English with the actual legal teeth. A solicitor
 * should review before public launch — placeholder names/dates are clearly
 * marked with [PLACEHOLDER] for the founder to update.
 */
export default function Terms() {
  return (
    <div className="min-h-screen">
      <header className="border-b-[1.5px] border-[var(--brand-ink)] grid-paper">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="terms-back-home">
            <VaultLogo size={24}/>
            <span className="font-display text-2xl font-semibold tracking-tight">JobVault</span>
          </Link>
          <Link to="/" className="font-mono-d text-xs text-[var(--brand-muted)] hover:text-[var(--brand-ink)]">/back</Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <p className="label-tiny">/terms of service</p>
        <h1 className="font-display text-4xl md:text-5xl tracking-tight font-semibold mt-2 mb-2">
          the deal.
        </h1>
        <p className="text-sm text-[var(--brand-muted)] font-mono-d mb-10">
          last updated: february 2026 · governed by the laws of England &amp; Wales
        </p>

        <section className="card-hard rounded-md p-6 bg-white mb-6">
          <h2 className="font-display text-2xl tracking-tight mb-3">the short version</h2>
          <ul className="text-sm space-y-2 list-disc pl-5 leading-relaxed">
            <li>you can use JobVault for free for personal job hunting.</li>
            <li>your vault contents belong to you — we just store them.</li>
            <li>be honest in what you upload. no other people&apos;s personal data without their permission.</li>
            <li>we can terminate accounts that abuse the service (spam, scraping, fraud).</li>
            <li>JobVault is provided &ldquo;as is&rdquo; — we&apos;ll try our best but can&apos;t guarantee you&apos;ll land the job.</li>
          </ul>
        </section>

        <section className="mb-6">
          <h2 className="font-display text-2xl tracking-tight mb-3">1. who we are</h2>
          <p className="text-sm leading-relaxed">
            &ldquo;JobVault&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo; refers to <b>[JobVault Ltd / Sole Trader — REPLACE WITH LEGAL ENTITY]</b>,
            registered in England &amp; Wales (company number <b>[PLACEHOLDER]</b>), registered office at <b>[PLACEHOLDER]</b>.
            You can reach us at <a className="underline" href="mailto:hello@jobvault.co.uk">hello@jobvault.co.uk</a>.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="font-display text-2xl tracking-tight mb-3">2. eligibility</h2>
          <p className="text-sm leading-relaxed mb-2">
            By creating an account you confirm that:
          </p>
          <ul className="text-sm space-y-1 list-disc pl-5 leading-relaxed">
            <li>you are at least 16 years old (the UK GDPR digital age of consent);</li>
            <li>you have the legal capacity to enter into this contract;</li>
            <li>your email address is yours;</li>
            <li>the information you put into your vault is yours, or you have permission to use it (e.g. referee contact details).</li>
          </ul>
        </section>

        <section className="mb-6">
          <h2 className="font-display text-2xl tracking-tight mb-3">3. your account</h2>
          <ul className="text-sm space-y-1 list-disc pl-5 leading-relaxed">
            <li>you&apos;re responsible for keeping your password secure. use a unique one.</li>
            <li>one person, one account.</li>
            <li>if you suspect unauthorised access, tell us at <a className="underline" href="mailto:security@jobvault.co.uk">security@jobvault.co.uk</a> immediately.</li>
            <li>you can delete your account at any time from Settings (right to erasure, with a 14-day grace window in case you change your mind).</li>
          </ul>
        </section>

        <section className="mb-6">
          <h2 className="font-display text-2xl tracking-tight mb-3">4. your content</h2>
          <p className="text-sm leading-relaxed mb-2">
            Everything you put into your vault — personal information, work history, documents, AI-generated analyses, applications — stays <b>yours</b>.
            You grant us a narrow, non-exclusive licence to store, process and display it back to you so the service works.
            We don&apos;t use your vault to train AI models, sell it, share it with recruiters, or anything similar. Ever.
          </p>
          <p className="text-sm leading-relaxed">
            If you share your public vault link (`/v/yourname`), you grant a public view-only licence for that page.
            You can revoke it at any time from Settings.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="font-display text-2xl tracking-tight mb-3">5. acceptable use</h2>
          <p className="text-sm leading-relaxed mb-2">Don&apos;t:</p>
          <ul className="text-sm space-y-1 list-disc pl-5 leading-relaxed">
            <li>upload anyone else&apos;s personal data without their consent (e.g. don&apos;t add a referee&apos;s phone number without asking them);</li>
            <li>use JobVault to apply for jobs you don&apos;t intend to take (autofill spam);</li>
            <li>scrape, reverse-engineer, or attempt to break the service;</li>
            <li>upload malware, illegal content, or anything that infringes IP;</li>
            <li>impersonate another person or misrepresent your qualifications.</li>
          </ul>
          <p className="text-sm leading-relaxed mt-2">
            We may suspend or terminate accounts that violate this section. We&apos;ll let you know why before we do, unless that&apos;s impractical or unlawful.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="font-display text-2xl tracking-tight mb-3">6. AI features</h2>
          <p className="text-sm leading-relaxed">
            Vault Coach (CV Tailor, Cover Letter Checker, Interview Prep) and the Culture Barometer use third-party large-language-models — currently Anthropic Claude — to generate suggestions.
            We disclose this on first use and you can decline. AI output is <b>guidance, not advice</b>. We don&apos;t guarantee its accuracy. You remain responsible for what you submit to employers.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="font-display text-2xl tracking-tight mb-3">7. paid plans &amp; cancellation</h2>
          <p className="text-sm leading-relaxed">
            Premium and Unlimited plans bill monthly or annually via Stripe. You can cancel any time — your plan stays active until the end of the paid period, then drops to Free.
            We don&apos;t offer pro-rata refunds for partially used months unless required by law. Annual plans within the first 14 days are refundable on request to <a className="underline" href="mailto:hello@jobvault.co.uk">hello@jobvault.co.uk</a> (UK consumer contract right of cancellation).
          </p>
        </section>

        <section className="mb-6">
          <h2 className="font-display text-2xl tracking-tight mb-3">8. service availability</h2>
          <p className="text-sm leading-relaxed">
            We aim for high uptime but don&apos;t promise it. Things break. We may need to perform maintenance or roll out fixes that briefly interrupt the service. We&apos;ll try to schedule it sensibly.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="font-display text-2xl tracking-tight mb-3">9. liability</h2>
          <p className="text-sm leading-relaxed">
            To the maximum extent permitted by law, our total liability to you for any claim arising from your use of JobVault is limited to (a) the fees you paid us in the 12 months before the claim, or (b) £100, whichever is greater.
            Nothing in these terms excludes liability for death, personal injury caused by negligence, fraud, or anything else that can&apos;t be excluded under UK law.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="font-display text-2xl tracking-tight mb-3">10. changes</h2>
          <p className="text-sm leading-relaxed">
            If we materially change these terms (e.g. price changes, new acceptable-use rules), we&apos;ll email you at least 14 days before the change takes effect.
            Continuing to use JobVault after that date means you accept the new terms. If you don&apos;t agree, you can delete your account.
          </p>
        </section>

        <section className="mb-6">
          <h2 className="font-display text-2xl tracking-tight mb-3">11. complaints &amp; disputes</h2>
          <p className="text-sm leading-relaxed">
            Got a complaint? Email <a className="underline" href="mailto:hello@jobvault.co.uk">hello@jobvault.co.uk</a> and we&apos;ll respond within 5 working days.
            If we can&apos;t resolve it, you can raise a complaint with the UK ICO (privacy issues) or take the matter to the courts of England &amp; Wales (other issues).
          </p>
        </section>

        <section className="card-hard rounded-md p-6 bg-[var(--pastel-yellow)] mb-12">
          <h2 className="font-display text-2xl tracking-tight mb-3">12. contact</h2>
          <p className="text-sm leading-relaxed">
            General: <a className="underline font-medium" href="mailto:hello@jobvault.co.uk">hello@jobvault.co.uk</a>
            <br/>Privacy / data requests: <a className="underline font-medium" href="mailto:privacy@jobvault.co.uk">privacy@jobvault.co.uk</a>
            <br/>Security: <a className="underline font-medium" href="mailto:security@jobvault.co.uk">security@jobvault.co.uk</a>
          </p>
        </section>

        <p className="text-xs font-mono-d text-[var(--brand-muted)] mb-10">
          By creating an account you accept these terms. See also our{" "}
          <Link to="/privacy" className="underline">Privacy Policy</Link> and{" "}
          <Link to="/cookies" className="underline">Cookie Policy</Link>.
        </p>
      </main>

      <footer className="border-t-[1.5px] border-[var(--brand-ink)] py-6 text-center font-mono-d text-xs text-[var(--brand-muted)]">
        © JobVault {new Date().getFullYear()} · made with love &amp; rage in the UK
      </footer>
    </div>
  );
}
